import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useOperationalStore from '../platform/store/useOperationalStore';
import './CalendarView.css';

/**
 * Appointments calendar — Decisiv-style month/week/day views.
 * Reads and writes the same localStorage store as the Red Book
 * (rekonScheduleData): { 'YYYY-MM-DD': { lines: [entry] } }.
 * Entries may carry optional time ('HH:MM'), bay, techNumber — added here,
 * ignored harmlessly by the classic Red Book.
 */
const STORAGE_KEY = 'rekonScheduleData';
const LEGACY_KEY  = 'redBookData';

function dateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfWeek(d) {
  const out = new Date(d);
  out.setDate(out.getDate() - out.getDay());
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function loadSchedule() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSchedule(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function entriesFor(data, key) {
  return (data[key]?.lines || []).filter(l => l.repairOrder || l.companyName);
}

// ─── New appointment modal ────────────────────────────────────────────────────

function NewAppointmentModal({ date, techs, onClose, onSave }) {
  const [form, setForm] = useState({
    date: dateKey(date),
    time: '',
    companyName: '',
    reason: '',
    bay: '',
    techNumber: '',
  });

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.companyName.trim() || !form.reason.trim()) return;
    onSave(form);
    onClose();
  }

  return (
    <div className="cal-modal-overlay" onClick={onClose}>
      <form className="cal-modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h3 className="cal-modal-title">New Appointment</h3>

        <label className="cal-field">
          <span>Date</span>
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
        </label>

        <label className="cal-field">
          <span>Time <em>(optional — blank = all day)</em></span>
          <input type="time" value={form.time} onChange={e => set('time', e.target.value)} />
        </label>

        <label className="cal-field">
          <span>Customer / Company</span>
          <input
            type="text" autoFocus value={form.companyName}
            onChange={e => set('companyName', e.target.value)}
            placeholder="F.W. Webb Central Distribution" required
          />
        </label>

        <label className="cal-field">
          <span>Reason</span>
          <input
            type="text" value={form.reason}
            onChange={e => set('reason', e.target.value)}
            placeholder="PM service, check engine light..." required
          />
        </label>

        <div className="cal-field-row">
          <label className="cal-field">
            <span>Bay <em>(optional)</em></span>
            <input type="text" value={form.bay} onChange={e => set('bay', e.target.value)} placeholder="3" />
          </label>
          <label className="cal-field">
            <span>Mechanic <em>(optional)</em></span>
            <select value={form.techNumber} onChange={e => set('techNumber', e.target.value)}>
              <option value="">Unassigned</option>
              {techs.map(t => (
                <option key={t.techNumber} value={t.techNumber}>
                  {t.name ? `${t.name} (#${t.techNumber})` : `#${t.techNumber}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="cal-modal-actions">
          <button type="button" className="cal-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="cal-btn cal-btn--primary">Save Appointment</button>
        </div>
      </form>
    </div>
  );
}

// ─── Appointment chip ─────────────────────────────────────────────────────────

function AppointmentChip({ entry, onClick }) {
  return (
    <button
      className={`cal-chip${entry.repairOrder ? ' cal-chip--ro' : ''}`}
      title={`${entry.companyName || ''} — ${entry.reason || ''}${entry.bay ? ` · Bay ${entry.bay}` : ''}${entry.techNumber ? ` · Tech ${entry.techNumber}` : ''}`}
      onClick={onClick}
    >
      {entry.time && <span className="cal-chip-time">{entry.time}</span>}
      <span className="cal-chip-name">{entry.repairOrder ? `${entry.repairOrder} · ` : ''}{entry.companyName}</span>
    </button>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function CalendarView() {
  const [view, setView]         = useState('week'); // 'month' | 'week' | 'day'
  const [anchor, setAnchor]     = useState(() => new Date());
  const [data, setData]         = useState(loadSchedule);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterBay, setFilterBay]   = useState(null);
  const [filterTech, setFilterTech] = useState(null);
  const trucks       = useOperationalStore(s => s.trucks);
  const technicians  = useOperationalStore(s => s.technicians);
  const setSelected  = useOperationalStore(s => s.setSelectedTruckId);
  const navigate = useNavigate();

  // Pick up Red Book edits made in other tabs/components
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setData(loadSchedule());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const today = new Date();
  const todayKey = dateKey(today);

  // Distinct bays present in visible data (for filter chips)
  const allBays = useMemo(() => {
    const set = new Set();
    Object.values(data).forEach(page =>
      (page.lines || []).forEach(l => { if (l.bay) set.add(String(l.bay)); })
    );
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [data]);

  const getEntries = useCallback((key) => {
    let list = entriesFor(data, key);
    if (filterBay)  list = list.filter(l => String(l.bay || '') === filterBay);
    if (filterTech === 'unassigned') list = list.filter(l => !l.techNumber);
    else if (filterTech) list = list.filter(l => String(l.techNumber || '') === filterTech);
    return list.sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99'));
  }, [data, filterBay, filterTech]);

  function openEntry(entry) {
    if (!entry.repairOrder) return;
    const truck = trucks.find(t => t.repairOrder === entry.repairOrder);
    if (truck) {
      setSelected(truck.id);
      navigate(`/ro/${truck.id}`);
    }
  }

  function handleSave(form) {
    setData(prev => {
      const page = prev[form.date] || { lines: [] };
      const entry = {
        id: `appt-${Date.now()}`,
        position: (page.lines || []).length,
        repairOrder: null,
        companyName: form.companyName.trim(),
        reason: form.reason.trim(),
        time: form.time || null,
        bay: form.bay.trim() || null,
        techNumber: form.techNumber || null,
        highlight: null,
        carryoverDays: null,
      };
      const next = { ...prev, [form.date]: { ...page, lines: [...(page.lines || []), entry] } };
      saveSchedule(next);
      return next;
    });
  }

  function shift(n) {
    setAnchor(a => {
      if (view === 'month') { const d = new Date(a); d.setMonth(d.getMonth() + n); return d; }
      return addDays(a, view === 'week' ? n * 7 : n);
    });
  }

  // ── Title per view ──
  const title = useMemo(() => {
    if (view === 'month') {
      return anchor.toLocaleDateString([], { month: 'long', year: 'numeric' });
    }
    if (view === 'week') {
      const s = startOfWeek(anchor);
      const e = addDays(s, 6);
      const sStr = s.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const eStr = e.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
      return `${sStr} – ${eStr}`;
    }
    return anchor.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }, [view, anchor]);

  // ── Week view: one column per day, all appointments stacked (timed first) ──
  function renderWeek() {
    const s = startOfWeek(anchor);
    const days = [...Array(7)].map((_, i) => addDays(s, i));

    return (
      <div className="cal-week">
        <div className="cal-week-head">
          {days.map(d => {
            const key = dateKey(d);
            const count = getEntries(key).length;
            return (
              <button
                key={key}
                className={`cal-day-head${key === todayKey ? ' cal-day-head--today' : ''}`}
                onClick={() => { setAnchor(d); setView('day'); }}
                title="Open day view"
              >
                <span className="cal-day-name">
                  {d.toLocaleDateString([], { weekday: 'short' }).toUpperCase()} {d.getMonth() + 1}/{d.getDate()}
                </span>
                <span className="cal-day-count">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="cal-week-cols">
          {days.map(d => {
            const key = dateKey(d);
            const entries = getEntries(key);
            return (
              <div key={key} className={`cal-day-col${key === todayKey ? ' cal-cell--today' : ''}`}>
                {entries.length === 0 && <span className="cal-empty-hint">—</span>}
                {entries.map((e, i) => (
                  <AppointmentChip key={e.id || i} entry={e} onClick={() => openEntry(e)} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Month view ──
  function renderMonth() {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const cells = [...Array(42)].map((_, i) => addDays(gridStart, i));

    return (
      <div className="cal-month">
        <div className="cal-month-head">
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
            <div key={d} className="cal-month-dow">{d}</div>
          ))}
        </div>
        <div className="cal-month-grid">
          {cells.map(d => {
            const key = dateKey(d);
            const entries = getEntries(key);
            const inMonth = d.getMonth() === anchor.getMonth();
            return (
              <div
                key={key}
                className={`cal-month-cell${inMonth ? '' : ' cal-month-cell--out'}${key === todayKey ? ' cal-cell--today' : ''}`}
                onClick={() => { setAnchor(d); setView('day'); }}
              >
                <div className="cal-month-date">
                  <span>{d.getDate()}</span>
                  {entries.length > 0 && <span className="cal-month-count">{entries.length}</span>}
                </div>
                <div className="cal-month-chips">
                  {entries.slice(0, 3).map((e, i) => (
                    <AppointmentChip key={e.id || i} entry={e} onClick={(ev) => { ev?.stopPropagation?.(); openEntry(e); }} />
                  ))}
                  {entries.length > 3 && <span className="cal-more">+{entries.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Day view: single stacked list, timed entries first ──
  function renderDay() {
    const key = dateKey(anchor);
    const entries = getEntries(key);

    return (
      <div className="cal-day">
        <div className="cal-day-list">
          {entries.length === 0 && (
            <div className="cal-empty-hint cal-empty-hint--day">No appointments scheduled this day.</div>
          )}
          {entries.map((e, i) => (
            <AppointmentChip key={e.id || i} entry={e} onClick={() => openEntry(e)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="cal-root">
      <div className="cal-header">
        <h2 className="cal-title">Appointments</h2>
        <button className="cal-btn cal-btn--primary" onClick={() => setModalOpen(true)}>
          + New Appointment
        </button>
      </div>

      <div className="cal-toolbar">
        {/* Filters: bay / mechanic / unassigned */}
        <div className="cal-filters">
          {allBays.map(b => (
            <button
              key={b}
              className={`cal-filter-chip${filterBay === b ? ' cal-filter-chip--active' : ''}`}
              onClick={() => setFilterBay(filterBay === b ? null : b)}
            >
              Bay {b}
            </button>
          ))}
          {technicians.slice(0, 8).map(t => {
            const num = String(t.number ?? t.techNumber ?? '');
            return (
              <button
                key={t.id || num}
                className={`cal-filter-chip${filterTech === num ? ' cal-filter-chip--active' : ''}`}
                onClick={() => setFilterTech(filterTech === num ? null : num)}
              >
                {t.name || `Tech ${num}`}
              </button>
            );
          })}
          <button
            className={`cal-filter-chip${filterTech === 'unassigned' ? ' cal-filter-chip--active' : ''}`}
            onClick={() => setFilterTech(filterTech === 'unassigned' ? null : 'unassigned')}
          >
            Unassigned
          </button>
        </div>

        {/* View switcher + nav */}
        <div className="cal-nav">
          {['month', 'week', 'day'].map(v => (
            <button
              key={v}
              className={`cal-view-btn${view === v ? ' cal-view-btn--active' : ''}`}
              onClick={() => setView(v)}
            >
              {v.toUpperCase()}
            </button>
          ))}
          <span className="cal-range-title">{title}</span>
          <button className="cal-btn cal-btn--icon" onClick={() => shift(-1)} title="Previous">‹</button>
          <button className="cal-btn cal-btn--icon" onClick={() => shift(1)} title="Next">›</button>
          <button className="cal-btn" onClick={() => setAnchor(new Date())}>Today</button>
        </div>
      </div>

      <div className="cal-body">
        {view === 'month' && renderMonth()}
        {view === 'week' && renderWeek()}
        {view === 'day' && renderDay()}
      </div>

      {modalOpen && (
        <NewAppointmentModal
          date={anchor}
          techs={technicians}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
